/**
 * 运行时模式服务
 * Runtime Mode Service
 *
 * 提供统一的运行时模式查询接口，使第三方模块能够感知当前运行环境。
 * Provides unified runtime mode query interface for third-party modules to be aware of current runtime environment.
 *
 * 模式定义 | Mode Definitions:
 * - Editor 模式：编辑器环境，显示网格、Gizmos、坐标轴等
 * - Playing 模式：游戏运行中（Play 按钮已按下）
 * - Preview 模式：预览模式（场景预览但不是完整的游戏运行）
 *
 * @example
 * ```typescript
 * import { RuntimeModeToken, type IRuntimeMode } from '@esengine/ecs-framework';
 *
 * // 获取服务
 * const runtimeMode = context.services.get(RuntimeModeToken);
 *
 * // 检查当前模式
 * if (runtimeMode?.isEditor) {
 *     // 编辑器特定逻辑
 * }
 *
 * // 监听模式变化
 * const unsubscribe = runtimeMode?.onModeChanged((mode) => {
 *     console.log('Mode changed:', mode.isPlaying ? 'Playing' : 'Stopped');
 * });
 * ```
 */
import { createServiceToken } from './PluginServiceRegistry';
import { createLogger } from '../Utils/Logger';
// ============================================================================
// 服务令牌 | Service Token
// ============================================================================
/**
 * 运行时模式服务令牌
 * Runtime mode service token
 */
export const RuntimeModeToken = createServiceToken('runtimeMode');
/**
 * 运行时模式服务默认实现
 * Default runtime mode service implementation
 */
export class RuntimeModeService {
    /**
     * 创建运行时模式服务
     * Create runtime mode service
     *
     * @param config 初始配置
     */
    constructor(config = {}) {
        this._callbacks = new Set();
        this._isEditor = config.isEditor ?? false;
        this._isPlaying = config.isPlaying ?? false;
        this._isPreview = config.isPreview ?? false;
    }
    // ========== IRuntimeMode 实现 ==========
    get isEditor() {
        return this._isEditor;
    }
    get isPlaying() {
        return this._isPlaying;
    }
    get isPreview() {
        return this._isPreview;
    }
    get isStandalone() {
        return !this._isEditor;
    }
    onModeChanged(callback) {
        this._callbacks.add(callback);
        return () => {
            this._callbacks.delete(callback);
        };
    }
    // ========== 设置方法（供运行时内部使用）==========
    /**
     * 设置编辑器模式
     * Set editor mode
     *
     * @internal
     */
    setEditorMode(isEditor) {
        if (this._isEditor !== isEditor) {
            this._isEditor = isEditor;
            this._notifyChange();
        }
    }
    /**
     * 设置播放状态
     * Set playing state
     *
     * @internal
     */
    setPlaying(isPlaying) {
        if (this._isPlaying !== isPlaying) {
            this._isPlaying = isPlaying;
            this._notifyChange();
        }
    }
    /**
     * 设置预览模式
     * Set preview mode
     *
     * @internal
     */
    setPreview(isPreview) {
        if (this._isPreview !== isPreview) {
            this._isPreview = isPreview;
            this._notifyChange();
        }
    }
    /**
     * 批量更新模式
     * Batch update mode
     *
     * @internal
     */
    updateMode(config) {
        let changed = false;
        if (config.isEditor !== undefined && this._isEditor !== config.isEditor) {
            this._isEditor = config.isEditor;
            changed = true;
        }
        if (config.isPlaying !== undefined && this._isPlaying !== config.isPlaying) {
            this._isPlaying = config.isPlaying;
            changed = true;
        }
        if (config.isPreview !== undefined && this._isPreview !== config.isPreview) {
            this._isPreview = config.isPreview;
            changed = true;
        }
        if (changed) {
            this._notifyChange();
        }
    }
    /**
     * @zh 通知模式变化
     * @en Notify mode change
     */
    _notifyChange() {
        for (const callback of this._callbacks) {
            try {
                callback(this);
            }
            catch (error) {
                RuntimeModeService._logger.error('Callback error:', error);
            }
        }
    }
    /**
     * 释放资源
     * Dispose resources
     */
    dispose() {
        this._callbacks.clear();
    }
}
RuntimeModeService._logger = createLogger('RuntimeModeService');
/**
 * 创建编辑器模式服务
 * Create editor mode service
 */
export function createEditorModeService() {
    return new RuntimeModeService({
        isEditor: true,
        isPlaying: false,
        isPreview: false
    });
}
/**
 * 创建独立运行时模式服务
 * Create standalone runtime mode service
 */
export function createStandaloneModeService() {
    return new RuntimeModeService({
        isEditor: false,
        isPlaying: true,
        isPreview: false
    });
}
//# sourceMappingURL=RuntimeModeService.js.map